import React from 'react';
import { GenericFormPage } from '../../components/generic';
import { useReminder, Reminder } from './useData';
import {
  moduleConfig,
  getInitialFormData,
  mapItemToFormData,
} from './config';

const Form: React.FC = () => {
  const configWithOptions = (() => {
    const fields = moduleConfig.fields.map((field) => {
      if (field.name === 'clientId') {
        return {
          ...field,
          type: 'select' as const,
          label: 'Client',
          placeholder: 'Select a client',
          options: [],
          apiEndpoint: '/crm-clients',
          labelField: 'clientName',
          valueField: 'clientId' as const,
        };
      }
      if (field.name === 'assignedTo') {
        return {
          ...field,
          type: 'select' as const,
          label: 'Assigned To',
          placeholder: 'Select user',
          options: [],
          apiEndpoint: '/users',
          labelField: 'userDisplayName',
          valueField: 'id' as const,
        };
      }
      if (field.name === 'clientReminderType') {
        return {
          ...field,
          type: 'select' as const,
          label: 'Type',
          placeholder: 'Select reminder type',
          options: [],
          apiEndpoint: '/p-crm-reminder-type',
          labelField: 'pr_descr',
          valueField: 'id' as const,
        };
      }
      return field;
    });

    // Form data allows foreign keys as string for select components
    type FormReminder = Omit<Reminder, 'clientId' | 'assignedTo' | 'clientReminderType'> & {
      clientId?: string | number;
      assignedTo?: string | number;
      clientReminderType?: string | number;
    };

    // Custom mapItemToFormData that converts foreign keys to string for select
    const customMapItemToFormData = (item: Reminder): Partial<FormReminder> => {
      const baseData = mapItemToFormData(item);
      return {
        ...baseData,
        clientId: baseData.clientId ? String(baseData.clientId) : '',
        assignedTo: baseData.assignedTo ? String(baseData.assignedTo) : '',
        clientReminderType: baseData.clientReminderType ? String(baseData.clientReminderType) : '',
      };
    };

    // Custom getInitialFormData
    const customGetInitialFormData = (): Partial<FormReminder> => {
      return {
        ...getInitialFormData(),
        clientId: '',
        assignedTo: '',
        clientReminderType: '',
      };
    };

    // Helper function to convert string to number or undefined
    const convertToNumber = (value: string | number | undefined): number | undefined => {
      if (value === undefined || value === null || String(value) === '') {
        return undefined;
      }
      return Number(value);
    };

    // Custom create/update handlers that convert foreign key strings back to numbers
    const customCreate = async (data: Partial<FormReminder>) => {
      const processedData: Partial<Reminder> = {
        ...data,
        clientId: convertToNumber(data.clientId) as number, // Required field
        assignedTo: convertToNumber(data.assignedTo) as number, // Required field
        clientReminderType: convertToNumber(data.clientReminderType) as number, // Required field
      };
      return await moduleConfig.form.create(processedData);
    };

    const customUpdate = async (id: number, data: Partial<FormReminder>) => {
      const processedData: Partial<Reminder> = {
        ...data,
        clientId: convertToNumber(data.clientId) as number, // Required field
        assignedTo: convertToNumber(data.assignedTo) as number, // Required field
        clientReminderType: convertToNumber(data.clientReminderType) as number, // Required field
      };
      return await moduleConfig.form.update(id, processedData);
    };

    return {
      ...moduleConfig,
      fields,
      getInitialFormData: customGetInitialFormData as () => Partial<Reminder>,
      mapItemToFormData: customMapItemToFormData as (item: Reminder) => Partial<Reminder>,
      form: {
        ...moduleConfig.form,
        create: customCreate as (data: Partial<Reminder>) => Promise<Reminder>,
        update: customUpdate as (id: number, data: Partial<Reminder>) => Promise<Reminder>,
      },
    };
  })();

  return (
    <GenericFormPage<Reminder>
      useData={useReminder}
      config={configWithOptions}
    />
  );
};

export default Form;

